const FRACTION_TOLERANCE = 0.02;

export const DISPLAY_FRACTIONS = [
    { value: 0.25, label: '1/4', vulgar: '¼' },
    { value: 1 / 3, label: '1/3', vulgar: '⅓' },
    { value: 0.5, label: '1/2', vulgar: '½' },
    { value: 2 / 3, label: '2/3', vulgar: '⅔' },
    { value: 0.75, label: '3/4', vulgar: '¾' },
] as const;

export function decimalToFractionLabel(value: number): string | null {
    if (!Number.isFinite(value) || value <= 0) return null;

    const whole = Math.floor(value + FRACTION_TOLERANCE);
    const fractionalPart = value - whole;

    if (fractionalPart < FRACTION_TOLERANCE) {
        return whole > 0 ? String(whole) : null;
    }

    for (const fraction of DISPLAY_FRACTIONS) {
        if (Math.abs(fractionalPart - fraction.value) < FRACTION_TOLERANCE) {
            return whole > 0 ? `${whole} ${fraction.label}` : fraction.label;
        }
    }

    return null;
}

export function decimalToVulgarFractionLabel(value: number): string | null {
    if (!Number.isFinite(value) || value <= 0) return null;

    const whole = Math.floor(value + FRACTION_TOLERANCE);
    const fractionalPart = value - whole;

    if (fractionalPart < FRACTION_TOLERANCE) {
        return whole > 0 ? String(whole) : null;
    }

    for (const fraction of DISPLAY_FRACTIONS) {
        if (Math.abs(fractionalPart - fraction.value) < FRACTION_TOLERANCE) {
            return whole > 0 ? `${whole}${fraction.vulgar}` : fraction.vulgar;
        }
    }

    return null;
}

export function formatQuantityDisplay(value: number): string {
    const fractionLabel = decimalToFractionLabel(value);
    if (fractionLabel) return fractionLabel;
    return String(parseFloat(value.toFixed(2)));
}

export function formatQuantityVulgarDisplay(value: number): string {
    const vulgarLabel = decimalToVulgarFractionLabel(value);
    if (vulgarLabel) return vulgarLabel;
    return String(parseFloat(value.toFixed(2)));
}

export function parseQuantityInput(input: string): number | null {
    const trimmed = input.trim().replace(',', '.');
    if (!trimmed) return null;

    const mixedMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
        const denominator = Number(mixedMatch[3]);
        if (denominator === 0) return null;
        return Number(mixedMatch[1]) + Number(mixedMatch[2]) / denominator;
    }

    const fractionMatch = trimmed.match(/^(\d+)\/(\d+)$/);
    if (fractionMatch) {
        const denominator = Number(fractionMatch[2]);
        if (denominator === 0) return null;
        return Number(fractionMatch[1]) / denominator;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
}

export function snapToDisplayFraction(value: number): number {
    for (const fraction of DISPLAY_FRACTIONS) {
        if (Math.abs(value - fraction.value) < FRACTION_TOLERANCE) {
            return fraction.value;
        }
    }

    const whole = Math.floor(value + FRACTION_TOLERANCE);
    const fractionalPart = value - whole;
    for (const fraction of DISPLAY_FRACTIONS) {
        if (Math.abs(fractionalPart - fraction.value) < FRACTION_TOLERANCE) {
            return whole + fraction.value;
        }
    }

    return parseFloat(value.toFixed(2));
}
